<template>
	<BaseCard :is-editable="false" title="Begutachtung" :is-toggleable="false">
		<div class="space-y-4">
			<div class="grid grid-cols-2 break-words leading-none mb-2 max-w-full w-full">
				<div class="font-bold">{{ $t('appointment_agreed_at') }}:</div>

				<div class="flex items-center justify-between">
					<div>
						{{ appointmentLabel }}
					</div>

					<div class="progress-action-slot">
						<button
							v-if="canEditAppointment"
							@click="editAppointment"
							:title="appointmentActionTitle"
							class="bg-sp btn block btn-overview-edit progress-action-btn flex items-center justify-center text-white"
						>
							<font-awesome-icon :icon="['fas', appointmentAgreedAt ? 'edit' : 'plus']" />
						</button>
					</div>
				</div>
			</div>

			<div class="grid grid-cols-2 break-words leading-none mb-2 max-w-full w-full">
				<div class="font-bold">{{ $t('examined_at') }}:</div>

				<div class="flex items-center justify-between">
					<div>
						{{ examinationLabel }}
					</div>

					<div class="progress-action-slot">
						<button
							v-if="canEditExamination"
							@click="editExamination"
							:title="examinationActionTitle"
							:disabled="!canEditExamination"
							class="bg-sp btn block btn-overview-edit progress-action-btn flex items-center justify-center text-white"
							:class="{
								'opacity-50 cursor-not-allowed': !canEditExamination,
							}"
						>
							<font-awesome-icon :icon="['fas', examinedAt ? 'edit' : 'plus']" />
						</button>
					</div>
				</div>
			</div>

			<div class="grid grid-cols-2 break-words leading-none mb-2 max-w-full w-full">
				<div class="font-bold flex items-center">{{ $t('expertise') }}:</div>

				<div class="flex items-center justify-between">
					<div>
						{{ expertiseLabel }}
					</div>

					<div class="progress-action-slot">
						<button
							v-if="canOpenExpertiseDocuments"
							@click="openExpertiseDocuments"
							:title="expertiseActionTitle"
							class="bg-sp btn block btn-overview-edit progress-action-btn flex items-center justify-center text-white"
						>
							<font-awesome-icon :icon="['fas', hasExpertise ? 'file-alt' : 'upload']" />
						</button>
					</div>
				</div>
			</div>
		</div>
	</BaseCard>
</template>

<script>
import BaseCard from '@/components/damagecases/forms/BaseCard';
import BaseCardRow from '@/components/damagecases/forms/BaseCardRow';

import { getGermanDate } from '@/plugins/Utils';

export default {
	name: 'DamageAssessorCaseExaminationProgressCard',

	components: {
		BaseCard,
		BaseCardRow,
	},

	props: {
		caseId: {
			type: [Number, String],
			required: true,
		},

		appointmentAgreedAt: {
			default: null,
		},

		examinedAt: {
			default: null,
		},

		hasExpertise: {
			type: Boolean,
			default: false,
		},

		isEditable: {
			type: Boolean,
			default: true,
		},
	},

	computed: {
		appointmentLabel() {
			if (!this.appointmentAgreedAt) {
				return '-';
			}

			return getGermanDate(new Date(this.appointmentAgreedAt));
		},

		examinationLabel() {
			if (!this.examinedAt) {
				return '-';
			}

			return getGermanDate(new Date(this.examinedAt));
		},

		expertiseLabel() {
			if (this.hasExpertise) {
				return 'Hinterlegt';
			}

			if (this.examinedAt) {
				return 'Noch nicht hinterlegt';
			}

			return 'Nach Begutachtung verfügbar';
		},

		canEditAppointment() {
			return this.isEditable && !this.examinedAt;
		},

		canEditExamination() {
			return this.isEditable && !!this.appointmentAgreedAt && !this.examinedAt;
		},

		canOpenExpertiseDocuments() {
			return this.hasExpertise || (this.isEditable && !!this.examinedAt);
		},

		appointmentActionTitle() {
			return this.appointmentAgreedAt
				? this.$t('appointment_agreed_at').toString() + ' ' + this.$t('edit_low').toString()
				: this.$t('appointment_agreed_at').toString() +
						' ' +
						this.$t('add')
							.toString()
							.toLowerCase();
		},

		examinationActionTitle() {
			return this.examinedAt
				? this.$t('examined_at').toString() + ' ' + this.$t('edit_low').toString()
				: this.$t('examined_at').toString() +
						' ' +
						this.$t('add')
							.toString()
							.toLowerCase();
		},

		expertiseActionTitle() {
			return this.hasExpertise ? this.$t('expertise').toString() : this.$t('upload').toString();
		},
	},

	methods: {
		editAppointment() {
			if (!this.canEditAppointment) {
				return;
			}
			this.$router.push({
				name: 'changeAppointment',
				params: {
					id: this.caseId,
				},
			});
		},
		editExamination() {
			if (!this.canEditExamination) {
				return;
			}

			this.$router.push({
				name: 'changeExamination',

				params: {
					id: this.caseId,
				},
			});
		},
		openExpertiseDocuments() {
			this.$emit('openDocuments', 'damageassessor');
		},
	},
};
</script>

<style scoped>
.progress-action-slot {
	width: 32px;
	min-width: 32px;
	height: 32px;
}

.progress-action-btn {
	width: 32px;
	height: 32px;
	min-width: 32px;
	padding: 0;
	font-size: 0.75rem;
}
</style>
